import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { colors, shadows } from '../../styles/theme';
import { normalizeForSearch, searchMatchPre } from '../../utils/searchMatch';
import { getPresetRange, toIsoDate } from '../../utils/analytics/dateRange';
import { logError } from '../../utils/logError';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Anayasa M.1: marka listesi API'den (/api/markalar) beslenir. Aşağıdaki harita yalnız
// API erişilemezse devreye giren geçici fallback'tir (default kaynak değil).
const FIRMA_LABEL_FALLBACK = {
  'TORUNA_KASAP': 'Toruna Gıda',
  'INGA_BURGER': 'Inga Burger',
  'ALTI_USTU_KOFTE': 'Alti Ustu Kofte',
};

const formatCurrency = (val) => {
  if (val == null) return '-';
  return Number(val).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' TL';
};

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('tr-TR');
};

// Cockpit drill-down derin-link: ?tab=OZET&baslangic=&bitis= (firma kasıtlı yok sayılır —
// SAS firma-bağımsız). Mount'ta bir kez okunur.
const _initialFromUrl = () => {
  try {
    const sp = new URLSearchParams(window.location.search);
    const tab = sp.get('tab');
    return {
      tab: ['FATURA', 'TRANSFER', 'OZET'].includes(tab) ? tab : 'FATURA',
      baslangic: sp.get('baslangic') || '',
      bitis: sp.get('bitis') || '',
    };
  } catch {
    return { tab: 'FATURA', baslangic: '', bitis: '' };
  }
};

const SirketlerArasiSatislarPage = () => {
  const _url = _initialFromUrl();
  const [satislar, setSatislar] = useState([]);
  const [ozet, setOzet] = useState({ toplam_alis: 0, toplam_satis: 0, toplam_kar: 0, kesilmemis_fatura: 0 });
  const [loading, setLoading] = useState(true);
  // aktifTab: 'FATURA' (fatura bazlı, default) | 'TRANSFER' (transfer bazlı, internal_sales)
  const [aktifTab, setAktifTab] = useState(_url.tab);
  const [hedefFirma, setHedefFirma] = useState('');
  const [faturaDurumu, setFaturaDurumu] = useState('');
  const [baslangic, setBaslangic] = useState(_url.baslangic);
  const [bitis, setBitis] = useState(_url.bitis);
  const [malzemeler, setMalzemeler] = useState([]);
  const [markalar, setMarkalar] = useState([]); // Anayasa M.1: firma dropdown + etiket API'den
  const [filterMalzemeId, setFilterMalzemeId] = useState('');
  const [filterMalzemeSearch, setFilterMalzemeSearch] = useState('');
  const [filterMalzemeOpen, setFilterMalzemeOpen] = useState(false);
  const [faturaForm, setFaturaForm] = useState(null); // id of row being edited
  const [faturaNo, setFaturaNo] = useState('');
  const [faturaTarihi, setFaturaTarihi] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  // OZET sekmesi: hedef firma → ürün ağırlıklı ort. fiyat raporu
  const [rapor, setRapor] = useState({ gruplar: [], ozet: { toplam_satis: 0, kesilmemis_fatura: 0, urun_cesidi: 0, transfer_sale_ids: [], fatura_sale_ids: [] } });
  // Toplu fatura kesildi işaretleme modal state
  const [toplu, setToplu] = useState(null); // { label, t_ids, f_ids }
  const [topluNo, setTopluNo] = useState('');
  const [topluTarih, setTopluTarih] = useState(new Date().toISOString().split('T')[0]);

  const token = localStorage.getItem('access_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (aktifTab === 'OZET') {
        const params = new URLSearchParams();
        if (baslangic) params.append('baslangic', baslangic);
        if (bitis) params.append('bitis', bitis);
        if (hedefFirma) params.append('hedef_firma', hedefFirma);
        if (filterMalzemeId) params.append('malzeme_id', filterMalzemeId);
        if (faturaDurumu === 'kesilmis') params.append('fatura_kesildi', 'true');
        if (faturaDurumu === 'kesilmemis') params.append('fatura_kesildi', 'false');
        const qs = params.toString();
        const res = await fetch(`${API}/api/sirketler-arasi-satislar/ozet-rapor${qs ? '?' + qs : ''}`, { headers });
        if (res.ok) {
          const data = await res.json();
          setRapor({ gruplar: data.gruplar || [], ozet: data.ozet || {} });
        } else {
          setMessage({ type: 'error', text: `Özet rapor yüklenemedi (HTTP ${res.status}).` });
        }
        return; // finally setLoading(false) çalışır
      }
      if (aktifTab === 'TRANSFER') {
        // Transfer Bazlı: InternalSale endpoint'inden çek + SAS shape'ine normalize et
        const params = new URLSearchParams();
        if (faturaDurumu === 'kesilmis') params.append('fatura_kesildi', 'true');
        if (faturaDurumu === 'kesilmemis') params.append('fatura_kesildi', 'false');
        if (baslangic) params.append('date_from', baslangic);
        if (bitis) params.append('date_to', bitis);

        const qs = params.toString();
        const url = `${API}/api/inventory/internal-sales${qs ? '?' + qs : ''}`;
        const res = await fetch(url, { headers });
        if (res.ok) {
          const data = await res.json();
          const items = Array.isArray(data) ? data : [];
          // Frontend filtre: hedefFirma seçildiyse client-side filter
          const filtered = hedefFirma ? items.filter(s => s.hedef_firma === hedefFirma) : items;
          // Normalize: tek-kalem yapısını SAS shape'ine dönüştür
          const normalized = filtered.map(s => ({
            id: s.id,
            kaynak_tip: 'TRANSFER',
            transfer_id: s.transfer_id,
            transfer_no: `TR-${s.transfer_id}`,
            kaynak_firma: s.kaynak_firma,
            hedef_firma: s.hedef_firma,
            fatura_no: null,
            tarih: s.tarih ? s.tarih.split('T')[0] : null,
            toplam_alis: s.total_cost,
            toplam_satis: s.total_revenue,
            kar_tutari: s.theoretical_profit,
            kar_marji_pct: s.total_cost > 0 ? (s.theoretical_profit / s.total_cost) * 100 : 0,
            logo_fatura_kesildi: s.logo_fatura_kesildi,
            logo_fatura_no: s.logo_fatura_no,
            logo_fatura_tarihi: s.logo_fatura_tarihi,
            kalemler: [{
              malzeme_id: s.malzeme_id,
              malzeme_ad: s.malzeme_ad,
              malzeme_kod: s.malzeme_kod,
              miktar: s.quantity,
              birim: null,
              alis_birim_fiyat: s.unit_cost_price,
              satis_birim_fiyat: s.transfer_price,
            }],
          }));
          setSatislar(normalized);
          const toplam_alis = normalized.reduce((a, s) => a + (s.toplam_alis || 0), 0);
          const toplam_satis = normalized.reduce((a, s) => a + (s.toplam_satis || 0), 0);
          const toplam_kar = normalized.reduce((a, s) => a + (s.kar_tutari || 0), 0);
          const kesilmemis = normalized.filter(s => !s.logo_fatura_kesildi).length;
          setOzet({ toplam_alis, toplam_satis, toplam_kar, kesilmemis_fatura: kesilmemis });
        } else {
          setMessage({ type: 'error', text: `Transfer satışları yüklenemedi (HTTP ${res.status}).` });
        }
      } else {
        // Tümü (default) veya Fatura Bazlı: mevcut SAS endpoint'i
        const params = new URLSearchParams();
        if (aktifTab === 'FATURA') params.append('kaynak_tip', 'FATURA');
        if (hedefFirma) params.append('hedef_firma', hedefFirma);
        if (faturaDurumu === 'kesilmis') params.append('fatura_kesildi', 'true');
        if (faturaDurumu === 'kesilmemis') params.append('fatura_kesildi', 'false');
        if (baslangic) params.append('baslangic', baslangic);
        if (bitis) params.append('bitis', bitis);

        const qs = params.toString();
        const url = `${API}/api/sirketler-arasi-satislar${qs ? '?' + qs : ''}`;
        const res = await fetch(url, { headers });
        if (res.ok) {
          const data = await res.json();
          setSatislar(data.satislar || []);
          setOzet(data.ozet || { toplam_alis: 0, toplam_satis: 0, toplam_kar: 0, kesilmemis_fatura: 0 });
        } else {
          setMessage({ type: 'error', text: `Satışlar yüklenemedi (HTTP ${res.status}).` });
        }
      }
    } catch (err) {
      logError('Satislar yuklenemedi:', err);
      setMessage({ type: 'error', text: 'Bağlantı hatası — satışlar yüklenemedi.' });
    } finally {
      setLoading(false);
    }
  }, [aktifTab, hedefFirma, faturaDurumu, baslangic, bitis, filterMalzemeId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  // Malzeme listesi — bir kere yükle, filtre dropdown için
  useEffect(() => {
    fetch(`${API}/api/malzemeler`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => setMalzemeler(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Marka listesi — firma dropdown + etiket haritası (Anayasa M.1: hardcode değil)
  useEffect(() => {
    fetch(`${API}/api/markalar`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => setMarkalar(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // kod → ad etiket çözümleyici (API + fallback)
  const firmaLabel = useCallback((kod) => {
    const hit = markalar.find(m => m.kod === kod);
    return (hit && hit.ad) || FIRMA_LABEL_FALLBACK[kod] || kod;
  }, [markalar]);

  // Filtre autocomplete sonuçları
  const filterMalzemeResults = useMemo(() => {
    if (!filterMalzemeSearch || filterMalzemeSearch.length < 1) return [];
    const q = normalizeForSearch(filterMalzemeSearch);
    return malzemeler
      .filter(m => searchMatchPre(m.ad, q) || searchMatchPre(m.kod, q))
      .slice(0, 30);
  }, [malzemeler, filterMalzemeSearch]);

  const filterMalzemeSelected = filterMalzemeId
    ? malzemeler.find(m => String(m.id) === String(filterMalzemeId))
    : null;

  // Ürün filtresi seçildiyse: kalemler içinde eşleşen malzeme_id varsa satışı göster
  const filteredSatislar = useMemo(() => {
    if (!filterMalzemeId) return satislar;
    const mid = parseInt(filterMalzemeId, 10);
    return satislar.filter(s =>
      (s.kalemler || []).some(k => parseInt(k.malzeme_id, 10) === mid)
    );
  }, [satislar, filterMalzemeId]);

  // Ürün filtresi aktifse özet de filtrelenmiş satışlar üzerinden
  const filteredOzet = useMemo(() => {
    if (!filterMalzemeId) return ozet;
    const toplam_alis = filteredSatislar.reduce((a, s) => a + (s.toplam_alis || 0), 0);
    const toplam_satis = filteredSatislar.reduce((a, s) => a + (s.toplam_satis || 0), 0);
    const toplam_kar = filteredSatislar.reduce((a, s) => a + (s.kar_tutari || 0), 0);
    const kesilmemis_fatura = filteredSatislar.filter(s => !s.logo_fatura_kesildi).length;
    return { toplam_alis, toplam_satis, toplam_kar, kesilmemis_fatura };
  }, [filteredSatislar, filterMalzemeId, ozet]);


  // Ekranda aktif olan sekme ve filtrelenmiş veriyi CSV olarak indirir.
  // UTF-8 BOM + noktalı virgül: Türkçe Excel'de doğrudan düzgün açılır.
  const csvCell = (value) => {
    const s = value == null ? '' : String(value);
    return `"${s.replace(/"/g, '""')}"`;
  };

  const handleCsvExport = () => {
    let rows = [];
    let headersCsv = [];

    if (aktifTab === 'OZET') {
      headersCsv = [
        'Kaynak Firma',
        'Hedef Firma',
        'Ürün Kodu',
        'Ürün',
        'Miktar',
        'Birim',
        'Ağırlıklı Ort. Satış Fiyatı',
        'Toplam Tutar',
      ];

      rows = (rapor.gruplar || []).flatMap(grup =>
        (grup.urunler || []).map(u => [
          firmaLabel(grup.kaynak_firma),
          firmaLabel(grup.hedef_firma),
          u.malzeme_kod || '',
          u.malzeme_ad || '',
          Number(u.toplam_miktar || 0),
          u.birim || '',
          Number(u.agirlikli_ort_satis_fiyat || 0).toFixed(2),
          Number(u.toplam_satis || 0).toFixed(2),
        ])
      );
    } else {
      headersCsv = [
        'Tarih',
        'Kaynak Tipi',
        'Belge / Transfer No',
        'Kaynak Firma',
        'Hedef Firma',
        'Alış Toplamı',
        'Satış Toplamı',
        'Kar',
        'Kar %',
        'Fatura Durumu',
        'Logo Fatura No',
        'Logo Fatura Tarihi',
      ];

      rows = filteredSatislar.map(s => {
        const alis = Number(s.toplam_alis || 0);
        const satis = Number(s.toplam_satis || 0);
        const kar = satis - alis;
        const karYuzde = alis ? (kar / alis) * 100 : 0;

        return [
          s.tarih || '',
          s.kaynak_tip || '',
          s.kaynak_tip === 'TRANSFER'
            ? (s.transfer_no || (s.transfer_id ? `TR-${s.transfer_id}` : ''))
            : (s.fatura_no || ''),
          firmaLabel(s.kaynak_firma),
          firmaLabel(s.hedef_firma),
          alis.toFixed(2),
          satis.toFixed(2),
          kar.toFixed(2),
          karYuzde.toFixed(2),
          s.logo_fatura_kesildi ? 'Kesildi' : 'Kesilmedi',
          s.logo_fatura_no || '',
          s.logo_fatura_tarihi || '',
        ];
      });
    }

    if (rows.length === 0) {
      setMessage({ type: 'error', text: 'CSV için aktarılacak kayıt bulunamadı.' });
      return;
    }

    const csv = [
      headersCsv.map(csvCell).join(';'),
      ...rows.map(row => row.map(csvCell).join(';')),
    ].join('\r\n');

    const bom = '\uFEFF';
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    const sekmeAdi =
      aktifTab === 'TRANSFER' ? 'transfer-bazli' :
      aktifTab === 'OZET' ? 'ozet-rapor' :
      'fatura-bazli';

    const tarihEtiketi = `${baslangic || 'baslangic'}_${bitis || 'bugun'}`;

    a.href = url;
    a.download = `sirketler-arasi-satislar_${sekmeAdi}_${tarihEtiketi}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setMessage({ type: 'success', text: `${rows.length} kayıt CSV olarak kaydedildi.` });
  };

  const handleFaturaKesildi = async (id, kaynakTip) => {
    if (!faturaNo.trim()) {
      setMessage({ type: 'error', text: 'Logo fatura numarasi giriniz.' });
      return;
    }
    setSaving(true);
    // Transfer-bazlı satışlar internal-sales endpoint'ini kullanır
    const url = kaynakTip === 'TRANSFER'
      ? `${API}/api/inventory/internal-sales/${id}/fatura-kesildi`
      : `${API}/api/sirketler-arasi-satis/${id}/fatura-kesildi`;
    try {
      const res = await fetch(url, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ logo_fatura_no: faturaNo.trim(), logo_fatura_tarihi: faturaTarihi }),
      });
      if (res.ok) {
        setMessage({ type: 'success', text: 'Fatura kesildi olarak islendi.' });
        setFaturaForm(null);
        setFaturaNo('');
        setFaturaTarihi(new Date().toISOString().split('T')[0]);
        loadData();
      } else {
        const err = await res.json().catch(() => ({}));
        setMessage({ type: 'error', text: err.detail || 'Hata olustu.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Baglanti hatasi.' });
    } finally {
      setSaving(false);
    }
  };

  const handleTopluKesildi = async () => {
    if (!topluNo.trim()) { setMessage({ type: 'error', text: 'Logo fatura numarasi giriniz.' }); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/sirketler-arasi-satislar/toplu-fatura-kesildi`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transfer_sale_ids: toplu.t_ids, fatura_sale_ids: toplu.f_ids,
          logo_fatura_no: topluNo.trim(), logo_fatura_tarihi: topluTarih,
        }),
      });
      if (res.ok) {
        const b = await res.json();
        setMessage({ type: 'success', text: `${b.transfer_isaretlenen + b.fatura_isaretlenen} kayit fatura kesildi olarak isaretlendi.` });
        setToplu(null); setTopluNo('');
        setTopluTarih(new Date().toISOString().split('T')[0]);
        loadData();
      } else {
        const err = await res.json().catch(() => ({}));
        setMessage({ type: 'error', text: err.detail || 'Hata olustu.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Baglanti hatasi.' });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (message) {
      const t = setTimeout(() => setMessage(null), 4000);
      return () => clearTimeout(t);
    }
  }, [message]);

  // Toplu modal: Escape ile kapat (kaydetme sürerken kapatma)
  useEffect(() => {
    if (!toplu) return;
    const onKey = (e) => { if (e.key === 'Escape' && !saving) setToplu(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toplu, saving]);

  const toplamKar = filteredOzet.toplam_satis - filteredOzet.toplam_alis;

  // KPI kartları: OZET sekmesinde rapor özetinden, diğer sekmelerde işlem (fatura/transfer) özetinden beslenir
  const ozetKartlari = aktifTab === 'OZET'
    ? [
        { label: 'Toplam Satis', value: formatCurrency(rapor.ozet.toplam_satis || 0), bg: '#e3f2fd', border: '#1565c0' },
        { label: 'Toplam Kar', value: formatCurrency(rapor.ozet.toplam_kar || 0), bg: '#e8f5e9', border: '#2e7d32' },
        { label: 'Urun Cesidi', value: rapor.ozet.urun_cesidi || 0, bg: '#fff3e0', border: '#e65100' },
        { label: 'Kesilmemis Fatura', value: rapor.ozet.kesilmemis_fatura || 0, bg: '#ffebee', border: '#c62828' },
      ]
    : [
        { label: 'Toplam Satis', value: formatCurrency(filteredOzet.toplam_satis), bg: '#e3f2fd', border: '#1565c0' },
        { label: 'Toplam Kar', value: formatCurrency(toplamKar), bg: '#e8f5e9', border: '#2e7d32' },
        { label: 'Kesilmemis Fatura', value: filteredOzet.kesilmemis_fatura, bg: '#ffebee', border: '#c62828' },
      ];

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <h2 style={{ marginBottom: 20, color: '#1a1a2e' }}>Sirketler Arasi Satislar</h2>

      {message && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 16,
          background: message.type === 'success' ? '#d4edda' : '#f8d7da',
          color: message.type === 'success' ? '#155724' : '#721c24',
        }}>
          {message.text}
        </div>
      )}

      {/* KPI Cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {ozetKartlari.map(k => (
          <div key={k.label} style={cardStyle(k.bg, k.border)}>
            <div style={{ fontSize: 13, color: colors.textSecondary, marginBottom: 4 }}>{k.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: k.border }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs: Fatura Bazlı / Transfer Bazlı (2026-05-15) */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: `2px solid ${colors.border}` }}>
        {[
          { key: 'FATURA', label: 'Fatura Bazlı', icon: '🧾' },
          { key: 'TRANSFER', label: 'Transfer Bazlı', icon: '🚚' },
          { key: 'OZET', label: 'Özet Rapor', icon: '📊' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setAktifTab(tab.key)}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: aktifTab === tab.key ? 700 : 500,
              color: aktifTab === tab.key ? '#1565c0' : colors.textSecondary,
              borderBottom: aktifTab === tab.key ? '3px solid #1565c0' : '3px solid transparent',
              marginBottom: -2,
              transition: 'all 0.2s',
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={hedefFirma} onChange={e => setHedefFirma(e.target.value)}
          style={selectStyle}>
          <option value="">Tumu</option>
          {(markalar.length > 0
            ? markalar.map(m => ({ value: m.kod, label: m.ad }))
            : Object.keys(FIRMA_LABEL_FALLBACK).map(k => ({ value: k, label: FIRMA_LABEL_FALLBACK[k] }))
          ).map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select value={faturaDurumu} onChange={e => setFaturaDurumu(e.target.value)}
          style={selectStyle}>
          <option value="">Tumu</option>
          <option value="kesilmemis">Kesilmemis</option>
          <option value="kesilmis">Kesilmis</option>
        </select>
        <input type="date" value={baslangic} onChange={e => setBaslangic(e.target.value)}
          style={inputStyle} placeholder="Baslangic" />
        <input type="date" value={bitis} onChange={e => setBitis(e.target.value)}
          style={inputStyle} placeholder="Bitis" />
        <div style={{ position: 'relative', minWidth: 240 }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={filterMalzemeSelected ? `${filterMalzemeSelected.kod} - ${filterMalzemeSelected.ad}` : filterMalzemeSearch}
              onChange={e => {
                setFilterMalzemeSearch(e.target.value);
                setFilterMalzemeId('');
                setFilterMalzemeOpen(true);
              }}
              onFocus={() => setFilterMalzemeOpen(true)}
              onBlur={() => setTimeout(() => setFilterMalzemeOpen(false), 150)}
              placeholder="Ürün ara (kod veya ad)..."
              style={{ ...inputStyle, width: '100%', paddingRight: filterMalzemeId ? '28px' : '8px', cursor: 'text' }}
            />
            {filterMalzemeId && (
              <span
                onClick={() => { setFilterMalzemeId(''); setFilterMalzemeSearch(''); }}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#999', fontSize: 14 }}
              >✕</span>
            )}
          </div>
          {filterMalzemeOpen && filterMalzemeResults.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
              background: '#fff', border: `1px solid ${colors.border}`, borderRadius: '0 0 6px 6px',
              boxShadow: shadows.md, maxHeight: 240, overflowY: 'auto'
            }}>
              {filterMalzemeResults.map(m => (
                <div
                  key={m.id}
                  onMouseDown={() => {
                    setFilterMalzemeId(String(m.id));
                    setFilterMalzemeSearch('');
                    setFilterMalzemeOpen(false);
                  }}
                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13 }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ color: '#888', marginRight: 6 }}>{m.kod}</span>{m.ad}
                </div>
              ))}
            </div>
          )}
        </div>
        {aktifTab === 'OZET' && (
          <>
            <button type="button" onClick={() => { const r = getPresetRange('thisMonth'); setBaslangic(toIsoDate(r.start)); setBitis(toIsoDate(r.end)); }} style={btnStyle}>Bu Ay</button>
            <button type="button" onClick={() => { const r = getPresetRange('lastMonth'); setBaslangic(toIsoDate(r.start)); setBitis(toIsoDate(r.end)); }} style={btnStyle}>Geçen Ay</button>
          </>
        )}

        <button
          type="button"
          onClick={handleCsvExport}
          disabled={loading}
          style={{
            ...btnStyle,
            marginLeft: 'auto',
            background: '#2e7d32',
            color: '#fff',
            border: 'none',
            opacity: loading ? 0.6 : 1,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          📥 CSV Olarak Kaydet
        </button>
      </div>

      {/* Table */}
      {aktifTab === 'OZET' ? (
        loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: colors.textTertiary }}>Yukleniyor...</div>
        ) : rapor.gruplar.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: colors.textTertiary }}>Secili aralikta sirketler arasi satis bulunamadi.</div>
        ) : (
          <div>
            {((rapor.ozet.transfer_sale_ids?.length || 0) + (rapor.ozet.fatura_sale_ids?.length || 0)) > 0 && (
              <div style={{ marginBottom: 16, textAlign: 'right' }}>
                <button
                  onClick={() => setToplu({ label: 'Tüm rapor', t_ids: rapor.ozet.transfer_sale_ids || [], f_ids: rapor.ozet.fatura_sale_ids || [] })}
                  style={{ ...btnStyle, background: '#1565c0', color: '#fff', border: 'none' }}
                >
                  Tum raporu tek faturayla isaretle ({rapor.ozet.kesilmemis_fatura})
                </button>
              </div>
            )}
            {rapor.gruplar.map(grup => (
              <div key={grup.hedef_firma} style={{ marginBottom: 28, background: colors.surface, borderRadius: 12, boxShadow: shadows.md, overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', background: colors.backgroundAlt, borderBottom: `1px solid ${colors.border}` }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e' }}>
                    {firmaLabel(grup.kaynak_firma)} {'->'} {firmaLabel(grup.hedef_firma)}
                  </div>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                    <span style={{ fontSize: 13, color: colors.textSecondary }}>
                      Toplam: <b style={{ color: '#1565c0' }}>{formatCurrency(grup.firma_ozet.toplam_satis)}</b>
                    </span>
                    {grup.firma_ozet.kesilmemis_sayisi > 0 && (
                      <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 12, background: '#ffebee', color: '#c62828', fontWeight: 600 }}>
                        {grup.firma_ozet.kesilmemis_sayisi} kesilmemis
                      </span>
                    )}
                    {((grup.firma_ozet.transfer_sale_ids?.length || 0) + (grup.firma_ozet.fatura_sale_ids?.length || 0)) > 0 && (
                      <button
                        onClick={() => setToplu({ label: firmaLabel(grup.hedef_firma), t_ids: grup.firma_ozet.transfer_sale_ids, f_ids: grup.firma_ozet.fatura_sale_ids })}
                        style={btnStyle}
                      >
                        Bu subeye fatura kesildi
                      </button>
                    )}
                  </div>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#fafafa' }}>
                      {['Urun', 'Kod', 'Miktar', 'Birim', 'Ag. Ort. Satis Fiyati', 'Toplam Tutar'].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {grup.urunler.map(u => (
                      <tr key={u.malzeme_id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                        <td style={tdStyle}>{u.malzeme_ad || '-'}</td>
                        <td style={{ ...tdStyle, color: '#888' }}>{u.malzeme_kod || '-'}</td>
                        <td style={tdStyleRight}>{Number(u.toplam_miktar || 0).toLocaleString('tr-TR')}</td>
                        <td style={tdStyle}>{u.birim || '-'}</td>
                        <td style={tdStyleRight}>{formatCurrency(u.agirlikli_ort_satis_fiyat)}</td>
                        <td style={{ ...tdStyleRight, fontWeight: 600 }}>{formatCurrency(u.toplam_satis)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: colors.textTertiary }}>Yukleniyor...</div>
      ) : filteredSatislar.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: colors.textTertiary }}>Kayit bulunamadi.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: colors.surface, borderRadius: 12, overflow: 'hidden', boxShadow: shadows.md }}>
            <thead>
              <tr style={{ background: colors.backgroundAlt, position: 'sticky', top: 0 }}>
                {['Tarih', 'Kaynak', 'Yonelim', 'Alis Toplami', 'Satis Toplami', 'Kar', 'Kar %', 'Fatura Durumu', 'Islem'].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredSatislar.map(s => {
                const kar = (s.toplam_satis || 0) - (s.toplam_alis || 0);
                const karYuzde = s.toplam_alis ? ((kar / s.toplam_alis) * 100).toFixed(1) : '-';
                const kesildi = s.logo_fatura_kesildi;
                const isEditing = faturaForm === s.id;

                return (
                  <React.Fragment key={s.id}>
                    <tr
                      style={{ borderBottom: `1px solid ${colors.border}`, cursor: 'pointer', background: expandedRow === s.id ? '#f0f4ff' : '#fff' }}
                      onClick={() => setExpandedRow(expandedRow === s.id ? null : s.id)}
                      onMouseEnter={e => { if (expandedRow !== s.id) e.currentTarget.style.background = '#fafafa'; }}
                      onMouseLeave={e => { if (expandedRow !== s.id) e.currentTarget.style.background = '#fff'; }}
                    >
                      <td style={tdStyle}>{formatDate(s.tarih)}</td>
                      <td style={tdStyle}>
                        {s.kaynak_tip === 'TRANSFER' ? (
                          <span title="Transfer bazlı satış" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700,
                              background: '#fff3e0', color: '#e65100',
                            }}>🚚 TRANSFER</span>
                            <span style={{ fontSize: 12, color: '#666' }}>{s.transfer_no || `#${s.transfer_id}`}</span>
                          </span>
                        ) : (
                          <span title="Fatura bazlı satış" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700,
                              background: '#e3f2fd', color: '#1565c0',
                            }}>🧾 FATURA</span>
                            <span style={{ fontSize: 12, color: '#666' }}>{s.fatura_no || '-'}</span>
                          </span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {firmaLabel(s.kaynak_firma)} {'->'} {firmaLabel(s.hedef_firma)}
                      </td>
                      <td style={tdStyleRight}>{formatCurrency(s.toplam_alis)}</td>
                      <td style={tdStyleRight}>{formatCurrency(s.toplam_satis)}</td>
                      <td style={{ ...tdStyleRight, color: kar >= 0 ? '#2e7d32' : '#c62828' }}>{formatCurrency(kar)}</td>
                      <td style={tdStyleRight}>{karYuzde !== '-' ? '%' + karYuzde : '-'}</td>
                      <td style={tdStyle}>
                        <span style={{
                          display: 'inline-block', padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                          background: kesildi ? '#e8f5e9' : '#ffebee',
                          color: kesildi ? '#2e7d32' : '#c62828',
                        }}>
                          {kesildi ? 'Kesildi' : 'Kesilmedi'}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        {!kesildi && (
                          <button
                            onClick={() => {
                              if (isEditing) {
                                setFaturaForm(null);
                              } else {
                                setFaturaForm(s.id);
                                setFaturaNo('');
                                setFaturaTarihi(new Date().toISOString().split('T')[0]);
                              }
                            }}
                            style={btnStyle}
                          >
                            {isEditing ? 'Iptal' : 'Fatura Kesildi'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {isEditing && (
                      <tr style={{ background: '#fafafa' }}>
                        <td colSpan={9} style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                            <input
                              type="text"
                              placeholder="Logo Fatura No"
                              value={faturaNo}
                              onChange={e => setFaturaNo(e.target.value)}
                              style={{ ...inputStyle, flex: 1, minWidth: 160 }}
                            />
                            <input
                              type="date"
                              value={faturaTarihi}
                              onChange={e => setFaturaTarihi(e.target.value)}
                              style={inputStyle}
                            />
                            <button
                              onClick={() => handleFaturaKesildi(s.id, s.kaynak_tip)}
                              disabled={saving}
                              style={{ ...btnStyle, background: '#2e7d32', color: '#fff' }}
                            >
                              {saving ? 'Kaydediliyor...' : 'Kaydet'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                    {expandedRow === s.id && s.kalemler && s.kalemler.length > 0 && (
                      <tr>
                        <td colSpan={9} style={{ padding: 0, background: colors.backgroundAlt }}>
                          <div style={{ padding: '12px 20px 12px 48px' }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 8 }}>Kalem Detayları</div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                              <thead>
                                <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                                  <th style={{ padding: '6px 10px', textAlign: 'left', color: '#888', fontWeight: 600, fontSize: 11 }}>Malzeme</th>
                                  <th style={{ padding: '6px 10px', textAlign: 'left', color: '#888', fontWeight: 600, fontSize: 11 }}>Kod</th>
                                  <th style={{ padding: '6px 10px', textAlign: 'right', color: '#888', fontWeight: 600, fontSize: 11 }}>Miktar</th>
                                  <th style={{ padding: '6px 10px', textAlign: 'left', color: '#888', fontWeight: 600, fontSize: 11 }}>Birim</th>
                                  <th style={{ padding: '6px 10px', textAlign: 'right', color: '#888', fontWeight: 600, fontSize: 11 }}>Alış Fiyat</th>
                                  <th style={{ padding: '6px 10px', textAlign: 'right', color: '#888', fontWeight: 600, fontSize: 11 }}>Satış Fiyat</th>
                                  <th style={{ padding: '6px 10px', textAlign: 'right', color: '#888', fontWeight: 600, fontSize: 11 }}>Kar</th>
                                </tr>
                              </thead>
                              <tbody>
                                {s.kalemler.map((k, idx) => (
                                  <tr key={idx} style={{ borderBottom: `1px solid ${colors.border}` }}>
                                    <td style={{ padding: '6px 10px', fontWeight: 600 }}>{k.malzeme_ad || '-'}</td>
                                    <td style={{ padding: '6px 10px', color: '#888' }}>{k.malzeme_kod || '-'}</td>
                                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{k.miktar}</td>
                                    <td style={{ padding: '6px 10px' }}>{k.birim || '-'}</td>
                                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{formatCurrency(k.alis_birim_fiyat)}</td>
                                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>{formatCurrency(k.satis_birim_fiyat)}</td>
                                    <td style={{ padding: '6px 10px', textAlign: 'right', color: '#2e7d32', fontWeight: 600 }}>
                                      {formatCurrency((k.satis_birim_fiyat - k.alis_birim_fiyat) * k.miktar)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                    {expandedRow === s.id && (!s.kalemler || s.kalemler.length === 0) && (
                      <tr>
                        <td colSpan={9} style={{ padding: '12px 48px', background: colors.backgroundAlt, color: colors.textTertiary, fontSize: 13 }}>
                          Kalem detayı bulunamadı
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {toplu && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}
             onClick={() => !saving && setToplu(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 24, width: 420, maxWidth: '90vw', boxShadow: shadows.md }}>
            <h3 style={{ marginTop: 0, marginBottom: 8, fontSize: 16 }}>Toplu Logo Faturasi — {toplu.label}</h3>
            <p style={{ fontSize: 13, color: colors.textSecondary, marginTop: 0 }}>
              {toplu.t_ids.length + toplu.f_ids.length} kayit tek fatura no ile "kesildi" olarak isaretlenecek.
            </p>
            <input type="text" placeholder="Logo Fatura No" value={topluNo} onChange={e => setTopluNo(e.target.value)} style={{ ...inputStyle, width: '100%', marginBottom: 10, boxSizing: 'border-box' }} />
            <input type="date" value={topluTarih} onChange={e => setTopluTarih(e.target.value)} style={{ ...inputStyle, width: '100%', marginBottom: 16, boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setToplu(null)} disabled={saving} style={btnStyle}>Iptal</button>
              <button onClick={handleTopluKesildi} disabled={saving} style={{ ...btnStyle, background: '#2e7d32', color: '#fff', border: 'none' }}>
                {saving ? 'Kaydediliyor...' : 'Isaretle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const cardStyle = (bg, border) => ({
  flex: '1 1 200px',
  background: colors.surface,
  borderRadius: 12,
  padding: '16px 20px',
  boxShadow: shadows.md,
  borderLeft: `4px solid ${border}`,
  minWidth: 180,
});

const selectStyle = {
  padding: '8px 12px',
  borderRadius: 8,
  border: `1px solid ${colors.border}`,
  fontSize: 14,
  background: colors.surface,
};

const inputStyle = {
  padding: '8px 12px',
  borderRadius: 8,
  border: `1px solid ${colors.border}`,
  fontSize: 14,
};

const thStyle = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: 13,
  fontWeight: 600,
  color: '#555',
  borderBottom: '2px solid #e0e0e0',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '10px 16px',
  fontSize: 14,
  color: '#333',
};

const tdStyleRight = {
  ...tdStyle,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};

const btnStyle = {
  padding: '6px 14px',
  borderRadius: 8,
  border: '1px solid #1565c0',
  background: '#e3f2fd',
  color: '#1565c0',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

export default SirketlerArasiSatislarPage;
